// Pooled camera-facing particles drawn as one instanced draw call per batch. The simulation lives
// in one flat Float32Array (stride PS) so spawning, stepping and compacting allocate nothing.
//
// Particle kinds (fragment shader): see KIND.

import * as THREE from "three";

export const KIND = {
  /** Soft round puff. */
  PUFF: 0,
  /** Hot spark: small bright core with a glow. */
  SPARK: 1,
  /** Thin ring (shock wave). */
  RING: 2,
  /** Four-point muzzle star with a hot core. */
  STAR: 3,
  /** Smoke: soft puff eroded by noise. */
  SMOKE: 4,
  /** Hard-edged chip (debris). */
  CHIP: 5,
  /** Glass shard: a sharp sliver with a bright rim that glints as it spins. */
  SHARD: 6,
} as const;

// Per-particle fields (offsets into the flat array).
const F_X = 0;
const F_Y = 1;
const F_Z = 2;
const F_VX = 3;
const F_VY = 4;
const F_VZ = 5;
const F_AGE = 6;
const F_LIFE = 7;
const F_SIZE0 = 8;
const F_SIZE1 = 9;
const F_GROW = 10;
const F_ROT = 11;
const F_SPIN = 12;
const F_GRAV = 13;
const F_DRAG = 14;
const F_R0 = 15;
const F_G0 = 16;
const F_B0 = 17;
const F_R1 = 18;
const F_G1 = 19;
const F_B1 = 20;
const F_COLORT = 21;
const F_ALPHA = 22;
const F_FADEIN = 23;
const F_FADEOUT = 24;
const F_KIND = 25;
const F_SEED = 26;
const PS = 27;

/** Reusable spawn description (set the fields you need after `reset()`). */
export class ParticleSpec {
  kind: number = KIND.PUFF;
  x = 0;
  y = 0;
  z = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  life = 1;
  size0 = 0.2;
  size1 = 0.2;
  /** Seconds to grow from size0 to size1 (0 = over the whole life). */
  grow = 0;
  rot = 0;
  spin = 0;
  /** m/s^2 pulling down. */
  gravity = 0;
  /** Exponential velocity damping (1/s). */
  drag = 0;
  r0 = 1;
  g0 = 1;
  b0 = 1;
  r1 = 1;
  g1 = 1;
  b1 = 1;
  /** Seconds to blend the colour from c0 to c1 (0 = over the whole life). */
  colorTime = 0;
  alpha = 1;
  fadeIn = 0.02;
  /** Seconds before the end over which alpha falls to 0. */
  fadeOut = 0.3;

  reset(): this {
    this.kind = KIND.PUFF;
    this.x = this.y = this.z = 0;
    this.vx = this.vy = this.vz = 0;
    this.life = 1;
    this.size0 = this.size1 = 0.2;
    this.grow = 0;
    this.rot = 0;
    this.spin = 0;
    this.gravity = 0;
    this.drag = 0;
    this.r0 = this.g0 = this.b0 = 1;
    this.r1 = this.g1 = this.b1 = 1;
    this.colorTime = 0;
    this.alpha = 1;
    this.fadeIn = 0.02;
    this.fadeOut = 0.3;
    return this;
  }

  color(r: number, g: number, b: number): this {
    this.r0 = this.r1 = r;
    this.g0 = this.g1 = g;
    this.b0 = this.b1 = b;
    return this;
  }

  colorTo(r: number, g: number, b: number): this {
    this.r1 = r;
    this.g1 = g;
    this.b1 = b;
    return this;
  }
}

const VERT = /* glsl */ `
attribute vec3 iPos;
attribute vec4 iData;
attribute vec4 iColor;
uniform float uNearEnd;
varying vec2 vUv;
varying vec4 vColor;
varying float vKind;
varying float vSeed;
varying float vNear;
varying float vGlint;
void main() {
  float c = cos(iData.y);
  float s = sin(iData.y);
  vec2 p = position.xy;
  p = vec2(c * p.x - s * p.y, s * p.x + c * p.y) * iData.x;
  vec4 mv = viewMatrix * vec4(iPos, 1.0);
  mv.xy += p;
  gl_Position = projectionMatrix * mv;
  vUv = position.xy + 0.5;
  vColor = iColor;
  vKind = iData.z;
  vSeed = iData.w;
  vNear = uNearEnd > 0.0 ? smoothstep(0.15, uNearEnd, -mv.z) : 1.0;
  // A spinning shard catches the light at a few angles of its turn.
  vGlint = pow(abs(sin(iData.y * 1.3 + iData.w * 6.2831)), 12.0);
}
`;

const FRAG = /* glsl */ `
varying vec2 vUv;
varying vec4 vColor;
varying float vKind;
varying float vSeed;
varying float vNear;
varying float vGlint;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
// Signed distance-like value of p from the edge a -> b (positive on the left).
float edgeSide(vec2 a, vec2 b, vec2 p) {
  vec2 e = b - a;
  vec2 d = p - a;
  return (e.x * d.y - e.y * d.x) / length(e);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

void main() {
  vec2 q = vUv - 0.5;
  float d = length(q) * 2.0;
  float a;
  vec3 tint = vec3(1.0);
  if (vKind < 0.5) {
    a = 1.0 - smoothstep(0.15, 1.0, d);
    a *= a;
  } else if (vKind < 1.5) {
    a = exp(-d * d * 10.0) + 0.25 * (1.0 - smoothstep(0.0, 1.0, d));
    tint = mix(vec3(1.0), vec3(1.6, 1.4, 1.0), exp(-d * d * 16.0));
  } else if (vKind < 2.5) {
    float r = abs(d - 0.82);
    a = (1.0 - smoothstep(0.0, 0.16, r)) * (1.0 - smoothstep(0.86, 1.0, d));
  } else if (vKind < 3.5) {
    vec2 aq = abs(q) * 2.0;
    float cross4 = max(exp(-aq.x * 14.0) * (1.0 - aq.y), exp(-aq.y * 14.0) * (1.0 - aq.x));
    vec2 rq = vec2(q.x + q.y, q.x - q.y) * 1.41;
    vec2 ar = abs(rq) * 2.0;
    float diag = 0.55 * max(exp(-ar.x * 12.0) * (1.0 - ar.y), exp(-ar.y * 12.0) * (1.0 - ar.x));
    float core = exp(-d * d * 7.0);
    a = clamp(max(cross4, diag) + core, 0.0, 1.0);
    tint = mix(vec3(1.0), vec3(1.5, 1.45, 1.3), core);
  } else if (vKind < 4.5) {
    vec2 w = vUv * 3.0 + vSeed * 17.0;
    float n = vnoise(w) * 0.6 + vnoise(w * 2.3 + 5.0) * 0.4;
    float edge = 1.0 - smoothstep(0.1, 1.0, d + (n - 0.5) * 0.55);
    a = edge * edge;
    tint = vec3(0.86 + 0.28 * n);
  } else if (vKind < 5.5) {
    vec2 aq = abs(q) * 2.0;
    a = 1.0 - smoothstep(0.8, 0.95, max(aq.x, aq.y));
    tint = vec3(0.7 + 0.3 * (1.0 - vUv.y));
  } else {
    // A triangle with seed-dependent corners: the shard; the rim and the glint brighten it.
    vec2 p = q * 2.0;
    vec2 ca = vec2(-0.85 + 0.4 * vSeed, -0.9);
    vec2 cb = vec2(0.9, -0.5 + 0.6 * fract(vSeed * 7.0));
    vec2 cc = vec2(-0.3 + 0.6 * fract(vSeed * 13.0), 0.95);
    float inside = min(edgeSide(ca, cb, p), min(edgeSide(cb, cc, p), edgeSide(cc, ca, p)));
    float rim = 1.0 - smoothstep(0.0, 0.2, inside);
    a = smoothstep(0.0, 0.05, inside) * (0.5 + 0.5 * rim + vGlint);
    tint = vec3(0.8 + 0.6 * rim + 2.2 * vGlint);
  }
  gl_FragColor = vec4(vColor.rgb * tint, vColor.a * a * vNear);
  if (gl_FragColor.a < 0.004) discard;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

function upload(attr: THREE.InstancedBufferAttribute, count: number): void {
  attr.clearUpdateRanges();
  attr.addUpdateRange(0, count);
  attr.needsUpdate = true;
}

export class ParticleBatch {
  readonly mesh: THREE.Mesh;
  readonly capacity: number;
  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly data: Float32Array;
  private readonly iPos: THREE.InstancedBufferAttribute;
  private readonly iData: THREE.InstancedBufferAttribute;
  private readonly iColor: THREE.InstancedBufferAttribute;
  private count = 0;

  /** `nearFade`: fade particles out close to the camera (off for the viewmodel scene). */
  constructor(capacity: number, additive: boolean, name: string, nearFade = true) {
    this.capacity = capacity;
    this.data = new Float32Array(capacity * PS);
    this.geometry = new THREE.InstancedBufferGeometry();
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3),
    );
    this.geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this.iPos = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.iData = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.iColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    for (const a of [this.iPos, this.iData, this.iColor]) a.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("iPos", this.iPos);
    this.geometry.setAttribute("iData", this.iData);
    this.geometry.setAttribute("iColor", this.iColor);
    this.geometry.instanceCount = 0;

    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      fog: false,
      uniforms: { uNearEnd: { value: nearFade ? 1.2 : 0 } },
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = name;
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = additive ? 20 : 19;
    this.mesh.visible = false;
  }

  get active(): number {
    return this.count;
  }

  /** Spawns a particle; returns false when the pool is full (the new particle is dropped). */
  spawn(s: ParticleSpec): boolean {
    if (this.count >= this.capacity) return false;
    const d = this.data;
    const o = this.count * PS;
    d[o + F_X] = s.x;
    d[o + F_Y] = s.y;
    d[o + F_Z] = s.z;
    d[o + F_VX] = s.vx;
    d[o + F_VY] = s.vy;
    d[o + F_VZ] = s.vz;
    d[o + F_AGE] = 0;
    d[o + F_LIFE] = Math.max(s.life, 0.001);
    d[o + F_SIZE0] = s.size0;
    d[o + F_SIZE1] = s.size1;
    d[o + F_GROW] = s.grow;
    d[o + F_ROT] = s.rot;
    d[o + F_SPIN] = s.spin;
    d[o + F_GRAV] = s.gravity;
    d[o + F_DRAG] = s.drag;
    d[o + F_R0] = s.r0;
    d[o + F_G0] = s.g0;
    d[o + F_B0] = s.b0;
    d[o + F_R1] = s.r1;
    d[o + F_G1] = s.g1;
    d[o + F_B1] = s.b1;
    d[o + F_COLORT] = s.colorTime;
    d[o + F_ALPHA] = s.alpha;
    d[o + F_FADEIN] = s.fadeIn;
    d[o + F_FADEOUT] = s.fadeOut;
    d[o + F_KIND] = s.kind;
    d[o + F_SEED] = Math.random();
    this.count++;
    return true;
  }

  clear(): void {
    this.count = 0;
    this.geometry.instanceCount = 0;
  }

  /** Steps every particle and uploads the live ones (compacted at the front of the instance buffers). */
  update(dt: number): void {
    const d = this.data;
    const pos = this.iPos.array as Float32Array;
    const dat = this.iData.array as Float32Array;
    const col = this.iColor.array as Float32Array;
    let n = this.count;
    let i = 0;
    while (i < n) {
      const o = i * PS;
      const age = d[o + F_AGE] + dt;
      const life = d[o + F_LIFE];
      if (age >= life) {
        n--;
        if (i !== n) d.copyWithin(o, n * PS, n * PS + PS);
        continue;
      }
      d[o + F_AGE] = age;
      const drag = Math.exp(-d[o + F_DRAG] * dt);
      const vx = d[o + F_VX] * drag;
      const vy = d[o + F_VY] * drag - d[o + F_GRAV] * dt;
      const vz = d[o + F_VZ] * drag;
      d[o + F_VX] = vx;
      d[o + F_VY] = vy;
      d[o + F_VZ] = vz;
      const x = (d[o + F_X] += vx * dt);
      const y = (d[o + F_Y] += vy * dt);
      const z = (d[o + F_Z] += vz * dt);

      const grow = d[o + F_GROW];
      const gt = grow > 0 ? Math.min(age / grow, 1) : age / life;
      const ge = gt * (2 - gt); // ease out
      const size = d[o + F_SIZE0] + (d[o + F_SIZE1] - d[o + F_SIZE0]) * ge;
      const ct = d[o + F_COLORT];
      const cf = ct > 0 ? Math.min(age / ct, 1) : age / life;
      const fi = d[o + F_FADEIN];
      const fo = d[o + F_FADEOUT];
      const aIn = fi > 0 ? Math.min(age / fi, 1) : 1;
      const aOut = fo > 0 ? Math.min((life - age) / fo, 1) : 1;
      const alpha = d[o + F_ALPHA] * aIn * aOut;

      const p = i * 3;
      pos[p] = x;
      pos[p + 1] = y;
      pos[p + 2] = z;
      const q = i * 4;
      dat[q] = size;
      dat[q + 1] = d[o + F_ROT] + d[o + F_SPIN] * age;
      dat[q + 2] = d[o + F_KIND];
      dat[q + 3] = d[o + F_SEED];
      col[q] = d[o + F_R0] + (d[o + F_R1] - d[o + F_R0]) * cf;
      col[q + 1] = d[o + F_G0] + (d[o + F_G1] - d[o + F_G0]) * cf;
      col[q + 2] = d[o + F_B0] + (d[o + F_B1] - d[o + F_B0]) * cf;
      col[q + 3] = alpha;
      i++;
    }
    this.count = n;
    this.geometry.instanceCount = n;
    this.mesh.visible = n > 0;
    if (n > 0) {
      upload(this.iPos, n * 3);
      upload(this.iData, n * 4);
      upload(this.iColor, n * 4);
    }
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
